'use client';

import { useRef, useCallback, useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations, useLocale } from 'next-intl';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useToast } from '@boty/ui';
import { LandingLayout, VariantA, VariantB, VariantC, LandingHeader, LandingFooter, UseCasesSection } from '../../features/landing';
import { useLandingVariants } from '../../lib/useLandingVariants';
import { trackPageView, trackLandingPageVisit } from '../../lib/analytics';
import type { BotIdea } from '../../features/landing/content/botIdeas';
import {
  ContentInputArea,
  FileUploadZone,
  FilePreviewList,
  ImagePreviewList,
  ProcessingIndicator,
  UrlInputField,
  UrlPreviewList,
  UrlCrawlProgress,
  GoogleDocPreviewList,
  UnifiedContentPanel,
  LeftSidebarSourcesList,
  CollapsibleUploadPreview,
  SidebarProgressSection,
  type UseContentInputReturn,
  isImageFile,
  isGoogleDocUrl,
  SUPPORTED_IMAGE_TYPES,
  createPastedImageFile,
} from '../../features/content-input';
import { ClarificationQuestions } from '../../features/clarification';
import { TestingPhase, type ContentType } from '../../features/testing';
import { CelebrationModal } from '../../features/celebration';
import { useEngagement, SignUpPrompt } from '../../features/auth';
import { useContentSources } from '../../features/content-input/useContentSources';
import { useCreateTempBot, useFileUpload, useImageUpload, useUrlExtraction, useGoogleExtraction, useTextContentSave, useLossPreventionWarning, useReferralCapture, useUpdateRefreshSettings } from '../../hooks';
import { AutoRefreshPromptDialog } from '../../features/content-input/AutoRefreshPromptDialog';
import type { RefreshFrequency } from '../../features/content-input/AutoRefreshPromptDialog';
import { getBotIdFromStorage, saveBotIdToStorage, clearBotIdFromStorage, clearSessionFromStorage } from '../../lib/session';
import { useAuth } from '@boty/database';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';

// Bot creation phases
type CreationPhase = 'content-input' | 'clarification' | 'testing';

export default function Home() {
  const router = useRouter();
  const locale = useLocale();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const tContent = useTranslations('creator.contentInput');
  const tLanding = useTranslations('creator.landing');
  const tTesting = useTranslations('creator.testing');
  const tFileUpload = useTranslations('creator.fileUpload');
  const tImageUpload = useTranslations('creator.imageUpload');
  const tUrlExtraction = useTranslations('creator.urlExtraction');
  const tGoogleExtraction = useTranslations('creator.googleExtraction');
  const tContentRefresh = useTranslations('creator.contentRefresh');
  const { showToast } = useToast();
  const { user } = useAuth();

  // A/B Test Variant - PostHog-based (Story 6.1, 6.6)
  const { pageVariant: variant, isLoading: isVariantLoading } = useLandingVariants();

  // Referral tracking - capture ?ref= parameter from viral links (Story 6.5)
  const { referralBotId } = useReferralCapture();

  // Track current phase in the creation flow
  const [creationPhase, setCreationPhase] = useState<CreationPhase>('content-input');

  // Track form validity via state (refs don't trigger re-renders)
  const [isFormValid, setIsFormValid] = useState(false);

  // Track text content for auto-save
  const [textContent, setTextContent] = useState('');

  // Optimistic text entry shown immediately in unified list while saving
  const [optimisticText, setOptimisticText] = useState<{
    content: string;
    characterCount: number;
  } | null>(null);

  // Shared bot ID across all content sources (files, images, URLs)
  const [sharedBotId, setSharedBotId] = useState<string | null>(null);

  // Track if this is a session recovery (returning user with existing content)
  const [isSessionRecovery, setIsSessionRecovery] = useState(false);

  // Track if user returned from testing phase (to show "Return to Testing" button)
  const [returnedFromTesting, setReturnedFromTesting] = useState(false);

  // Track if user returned from clarification phase (to keep marketing content collapsed)
  const [returnedFromClarification, setReturnedFromClarification] = useState(false);

  // Track if user added new content (to show toast on return to testing)
  const [hasNewContent, setHasNewContent] = useState(false);

  // Content version - increment to trigger suggestion refresh in TestingPhase
  const [contentVersion, setContentVersion] = useState(0);

  // Track celebration modal visibility (shown after clarification phase)
  const [showCelebration, setShowCelebration] = useState(false);

  // Track test question count for engagement prompts (Story 3.8)
  const [testQuestionCount, setTestQuestionCount] = useState(0);

  // Auto-refresh prompt state (shown after URL extraction completes)
  const [refreshPrompt, setRefreshPrompt] = useState<{
    open: boolean;
    contentId: string;
    sourceUrl: string;
  }>({ open: false, contentId: '', sourceUrl: '' });

  // Inspiration feed hint state (Story 6.13)
  const [inspirationHint, setInspirationHint] = useState<string | null>(null);
  const [showCoralFlash, setShowCoralFlash] = useState(false);

  // Ref to access content input hook internals for form submission
  const contentInputRef = useRef<UseContentInputReturn | null>(null);

  // File upload state (documents only)
  const {
    files,
    isUploading: isUploadingFiles,
    uploadFiles: uploadDocuments,
    handleRejectedFiles,
    removeFile,
    removeByContentId: removeFileByContentId,
    botId: fileBotId,
    processWithAI,
  } = useFileUpload({
    // Use shared bot ID if available
    ...(sharedBotId && { botId: sharedBotId }),
    onError: (file) => {
      // Translate error message
      const errorKey = file.errorMessage as string;
      const translatedError = tFileUpload(`error.${errorKey}`);
      showToast(translatedError, 'error');
    },
  });

  // Image upload state
  const {
    images,
    isUploading: isUploadingImages,
    isProcessing: isProcessingImages,
    uploadImages,
    handleRejectedImages,
    removeImage,
    removeByContentId: removeImageByContentId,
    retryImage,
    botId: imageBotId,
    isMaxReached: isMaxImagesReached,
  } = useImageUpload({
    // Use shared bot ID if available
    ...(sharedBotId && { botId: sharedBotId }),
    onError: (image) => {
      // Translate error message
      const errorKey = image.errorMessage as string;
      const translatedError = tImageUpload(`error.${errorKey}`);
      showToast(translatedError, 'error');
    },
    onPartialSuccess: (image) => {
      // Image was stored but vision analysis failed
      const translatedWarning = tImageUpload('error.visionFailed');
      showToast(`${image.fileName}: ${translatedWarning}`, 'warning');
    },
  });

  // URL extraction state
  const {
    urls,
    isExtracting,
    currentCount,
    totalCount,
    summary: extractionSummary,
    extractUrl,
    removeUrl,
    removeByContentId: removeUrlByContentId,
    botId: urlBotId,
  } = useUrlExtraction({
    // Use shared bot ID if available
    ...(sharedBotId && { botId: sharedBotId }),
    onSuccess: (primaryUrl) => {
      // Show auto-refresh prompt for URL-based content
      if (primaryUrl.contentId && primaryUrl.url) {
        setRefreshPrompt({
          open: true,
          contentId: primaryUrl.contentId,
          sourceUrl: primaryUrl.url,
        });
      }
    },
    onError: (_url, error) => {
      // Translate error message based on error code
      const errorKey = error.error.code.toLowerCase();
      const translatedError = tUrlExtraction(`error.${errorKey}`);
      showToast(translatedError || error.error.message, 'error');
    },
  });

  // Google document extraction state
  const {
    contents: googleDocs,
    isExtracting: isExtractingGoogle,
    extractGoogleDoc,
    removeContent: removeGoogleDoc,
    removeByContentId: removeGoogleDocByContentId,
    botId: googleBotId,
  } = useGoogleExtraction({
    // Use shared bot ID if available
    ...(sharedBotId && { botId: sharedBotId }),
    onSuccess: (content) => {
      // Show auto-refresh prompt for Google Docs/Sheets too (parity with URL extraction)
      if (content.contentId && content.url) {
        setRefreshPrompt({
          open: true,
          contentId: content.contentId,
          sourceUrl: content.url,
        });
      }
    },
    onError: (_content, error) => {
      // Translate error message based on error code
      const errorKey = error.error.code;
      const translatedError = tGoogleExtraction(`error.${errorKey}`);
      showToast(translatedError || error.error.message, 'error');
    },
  });

  // Text content auto-save
  const {
    saveContent: saveTextContent,
    setEditingContent,
    resetEditing,
    isSaving: isSavingText,
    savedContentId: textSavedContentId,
    botId: textBotId,
    isEditing: isEditingText,
  } = useTextContentSave({
    botId: sharedBotId,
    debounceMs: 1500,
    minLength: 10,
    onSaved: ({ botId }) => {
      // Update shared bot ID if this created a new bot
      if (botId && !sharedBotId) {
        setSharedBotId(botId);
      }
    },
  });

  // Watch for text content changes and auto-save + update optimistic entry
  useEffect(() => {
    if (textContent && textContent.length >= 10) {
      saveTextContent(textContent);
      // Show optimistic entry immediately (only for new text, not edits)
      if (!isEditingText) {
        setOptimisticText(prev => {
          if (prev && prev.content === textContent) return prev;
          return { content: textContent, characterCount: textContent.length };
        });
      }
    } else {
      setOptimisticText(null);
    }
  }, [textContent, saveTextContent, isEditingText]);

  // Clear optimistic entry once the real content is saved and available
  useEffect(() => {
    if (textSavedContentId) {
      setOptimisticText(null);
    }
  }, [textSavedContentId]);

  // Load persisted bot ID on mount for session recovery
  // If the bot is already published (from a previous creation), clear storage
  // and redirect authenticated users to the dashboard instead of showing creation UI
  useEffect(() => {
    const persistedBotId = getBotIdFromStorage();
    if (!persistedBotId || sharedBotId) return;

    // Check if this bot is already published before recovering the session
    fetch(`/api/bots/${persistedBotId}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((result) => {
        if (result?.data?.status === 'published') {
          // Bot already published - clear stale creation data
          clearBotIdFromStorage();
          clearSessionFromStorage();
          // Redirect authenticated users to dashboard
          if (user) {
            router.push(`/${locale}/dashboard`);
          }
          // For unauthenticated users, just clear storage and show fresh landing
        } else {
          // Bot is still a draft (or temp) - recover the session
          setSharedBotId(persistedBotId);
          setIsSessionRecovery(true);
        }
      })
      .catch(() => {
        // API error (transient network issue, auth race after sign-up, etc.)
        // Do NOT clear storage here - the bot may still exist and be claimable.
        // Stale data is cleaned up when the bot is eventually published or on next successful check.
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Track page view with A/B variant (Story 6.1)
  useEffect(() => {
    if (!isVariantLoading) {
      trackPageView('landing');
    }
  }, [isVariantLoading]);

  // Track landing page visit with referral attribution (Story 6.5)
  useEffect(() => {
    if (referralBotId) {
      trackLandingPageVisit(referralBotId);
    }
  }, [referralBotId]);

  // Sync shared bot ID when any hook creates a new bot
  // This ensures all subsequent content uses the same bot
  useEffect(() => {
    const newBotId = fileBotId ?? imageBotId ?? urlBotId ?? googleBotId ?? textBotId;
    if (newBotId && !sharedBotId) {
      setSharedBotId(newBotId);
    }
  }, [fileBotId, imageBotId, urlBotId, googleBotId, textBotId, sharedBotId]);

  // Persist bot ID to localStorage for session recovery
  useEffect(() => {
    if (sharedBotId) {
      saveBotIdToStorage(sharedBotId);
    }
  }, [sharedBotId]);

  // Use the shared bot ID for all operations
  const combinedBotId = sharedBotId ?? fileBotId ?? imageBotId ?? urlBotId ?? googleBotId;

  // Auto-refresh settings mutation (for URL content refresh frequency)
  const updateRefreshSettings = useUpdateRefreshSettings(combinedBotId || '');

  // Handle auto-refresh prompt save
  const handleRefreshSave = useCallback((frequency: RefreshFrequency) => {
    if (frequency === 'none') {
      setRefreshPrompt({ open: false, contentId: '', sourceUrl: '' });
      return;
    }
    updateRefreshSettings.mutate(
      {
        contentId: refreshPrompt.contentId,
        refreshEnabled: true,
        refreshInterval: frequency,
      },
      {
        onSuccess: () => {
          showToast(tContentRefresh('settingsUpdated'), 'success');
          setRefreshPrompt({ open: false, contentId: '', sourceUrl: '' });
        },
        onError: () => {
          setRefreshPrompt({ open: false, contentId: '', sourceUrl: '' });
        },
      }
    );
  }, [refreshPrompt.contentId, updateRefreshSettings, showToast, tContentRefresh]);

  // Fetch content sources from database for engagement tracking (Story 3.8)
  const { sources: persistedSources } = useContentSources({
    botId: combinedBotId,
    enabled: !!combinedBotId,
  });

  // Engagement tracking for sign-up prompts (Story 3.8)
  const {
    shouldShowPrompt,
    promptType,
    dismissPrompt,
  } = useEngagement({
    botId: combinedBotId,
    uploadCount: persistedSources?.length ?? 0,
    questionCount: testQuestionCount,
    isAuthenticated: !!user,
  });

  // Combined uploading/extracting/saving state
  const isUploading = isUploadingFiles || isUploadingImages || isProcessingImages || isExtracting || isExtractingGoogle || isSavingText;

  // Create temp bot mutation
  const { mutate: createTempBot, isPending } = useCreateTempBot({
    onSuccess: () => {
      showToast(tContent('success'), 'success');
      // TODO: Navigate to bot testing page in future story
      // router.push(`/bot/${data.data.botId}/test`);
    },
    onError: (error) => {
      showToast(error.error.message || tContent('error.createFailed'), 'error');
    },
  });

  // Check if user has content (text, files, images, URLs, or Google docs)
  const readyFilesCount = files.filter((f) => f.status === 'ready').length;
  const readyImagesCount = images.filter((img) => img.status === 'ready').length;
  const readyUrlsCount = urls.filter((u) => u.status === 'ready' || u.status === 'partial').length;
  const readyGoogleDocsCount = googleDocs.filter((g) => g.status === 'ready').length;
  const hasFiles = readyFilesCount > 0;
  const hasImages = readyImagesCount > 0;
  const hasUrls = readyUrlsCount > 0;
  const hasGoogleDocs = googleDocs.length > 0;
  const hasContent = hasFiles || hasImages || hasUrls || hasGoogleDocs;

  // Loss prevention warning for unauthenticated users with content (Story 3.10)
  const hasUnsavedContent = hasContent || textContent.length >= 10 || !!sharedBotId;
  useLossPreventionWarning({
    hasUnsavedContent,
    isAuthenticated: !!user,
  });

  // Handle file upload - route to correct handler based on file type
  const handleFilesAccepted = useCallback(
    (acceptedFiles: File[]) => {
      // Separate images from documents
      const imageFiles = acceptedFiles.filter(isImageFile);
      const documentFiles = acceptedFiles.filter((f) => !isImageFile(f));

      // Upload documents
      if (documentFiles.length > 0) {
        uploadDocuments(documentFiles);
      }

      // Upload images (if not at max)
      if (imageFiles.length > 0 && !isMaxImagesReached) {
        uploadImages(imageFiles);
      } else if (imageFiles.length > 0 && isMaxImagesReached) {
        // Show error for images that couldn't be added
        showToast(tImageUpload('error.maxImagesExceeded'), 'error');
      }
    },
    [uploadDocuments, uploadImages, isMaxImagesReached, showToast, tImageUpload]
  );

  // Container-level paste handler: intercepts image pastes anywhere in the
  // content input area (including the text textarea) and routes them to upload
  const handleContentPaste = useCallback(
    (e: React.ClipboardEvent) => {
      const items = e.clipboardData?.files;
      if (!items || items.length === 0) return;

      const imageBlobs = Array.from(items).filter((file) =>
        SUPPORTED_IMAGE_TYPES.includes(file.type as typeof SUPPORTED_IMAGE_TYPES[number])
      );

      if (imageBlobs.length === 0) return;

      e.preventDefault();
      const imageFiles = imageBlobs.map(createPastedImageFile);
      handleFilesAccepted(imageFiles);
    },
    [handleFilesAccepted]
  );

  // Handle URL submission - route Google URLs to Google extraction
  const handleUrlSubmit = useCallback(
    (url: string) => {
      if (isGoogleDocUrl(url)) {
        extractGoogleDoc({ url });
      } else {
        extractUrl(url);
      }
    },
    [extractUrl, extractGoogleDoc]
  );

  // Handle content submission
  const handleSubmit = useCallback(
    (content: string) => {
      // If files/images/urls were uploaded, they already have a bot - add text to same bot
      // Otherwise create a new temp bot with the text content
      if (combinedBotId) {
        createTempBot({ content, botId: combinedBotId });
      } else {
        createTempBot({ content });
      }
    },
    [createTempBot, combinedBotId]
  );

  // Handle continue button click - move to clarification phase
  const handleContinue = useCallback(() => {
    // If we have text content, submit it first (will link to existing bot if files uploaded)
    if (contentInputRef.current && isFormValid) {
      contentInputRef.current.handleSubmit((data) => {
        handleSubmit(data.content);
      })();
    }

    // Move to clarification phase if we have a bot ID
    if (combinedBotId || hasContent) {
      window.scrollTo(0, 0);
      setReturnedFromClarification(false);
      setCreationPhase('clarification');
    }
  }, [handleSubmit, isFormValid, hasContent, combinedBotId]);

  // Handle clarification phase completion - show celebration modal
  const handleClarificationComplete = useCallback(() => {
    window.scrollTo(0, 0);
    setShowCelebration(true);
  }, []);

  // Handle celebration modal completion - proceed to testing phase
  const handleCelebrationProceed = useCallback(() => {
    window.scrollTo(0, 0);
    setShowCelebration(false);
    setCreationPhase('testing');
  }, []);

  // Handle testing phase completion - navigate to deploy (if logged in) or signup
  const handleTestingComplete = useCallback(() => {
    if (user) {
      // User is already logged in - go directly to deploy
      // Pass bot ID directly - more reliable than session lookup
      const deployUrl = combinedBotId
        ? `/${locale}/deploy?bid=${encodeURIComponent(combinedBotId)}`
        : `/${locale}/deploy`;
      router.push(deployUrl);
    } else {
      // User needs to sign up/login first - use quick-signup for minimal friction
      router.push(`/${locale}/quick-signup`);
    }
  }, [router, locale, user, combinedBotId]);

  // Handle share button click in celebration modal
  const handleShare = useCallback(() => {
    if (user) {
      // User is already logged in - go directly to deploy
      // Pass bot ID directly - more reliable than session lookup
      const deployUrl = combinedBotId
        ? `/${locale}/deploy?bid=${encodeURIComponent(combinedBotId)}`
        : `/${locale}/deploy`;
      router.push(deployUrl);
    } else {
      // User needs to sign up/login first
      router.push(`/${locale}/quick-signup`);
    }
  }, [router, locale, user, combinedBotId]);

  // Handle add content from testing phase - accepts content type for routing
  const handleAddContentFromTesting = useCallback((_type?: ContentType) => {
    setReturnedFromTesting(true);
    // Note: _type is available for future content tab routing if needed
    // Currently we just navigate to content-input phase
    setCreationPhase('content-input');
  }, []);

  // Handle return to testing from content-input phase
  const handleReturnToTesting = useCallback(() => {
    setReturnedFromTesting(false);
    setHasNewContent(true); // Will trigger toast
    setContentVersion((v) => v + 1); // Will trigger suggestion refresh
    setCreationPhase('testing');
  }, []);

  // Show toast when returning to testing with new content
  useEffect(() => {
    if (creationPhase === 'testing' && hasNewContent) {
      showToast(tTesting('contentUpdated'), 'success');
      setHasNewContent(false);
    }
  }, [creationPhase, hasNewContent, showToast, tTesting]);

  // Handle back from testing to clarification
  const handleBackFromTesting = useCallback(() => {
    setCreationPhase('clarification');
  }, []);

  // Handle going back from clarification to content input
  // Set returnedFromClarification so the page keeps the marketing/value props
  // collapsed (the user already has content and a bot, so showing the
  // landing layout would be jarring and incorrect).
  const handleBackToContent = useCallback(() => {
    setReturnedFromClarification(true);
    setCreationPhase('content-input');
  }, []);

  // Handle inspiration idea click (Story 6.13 - AC4)
  // Note: analytics tracking (trackInspirationIdeaClicked) is handled by
  // BotInspirationFeed component which has the correct feedPosition context.
  const handleIdeaClick = useCallback(
    (idea: BotIdea) => {
      // Scroll to content input area
      const target = document.getElementById('content-input');
      if (target) {
        const prefersReducedMotion = window.matchMedia(
          '(prefers-reduced-motion: reduce)',
        ).matches;
        target.scrollIntoView({
          behavior: prefersReducedMotion ? 'auto' : 'smooth',
        });
      }

      // Update placeholder hint with translated name (non-committal — do NOT pre-fill input)
      const ideaKey = idea.nameKey.split('.').pop() ?? '';
      const translatedName = tLanding(`inspirationFeed.ideas.${ideaKey}`);
      setInspirationHint(translatedName);

      // Coral border flash (400ms for visibility)
      setShowCoralFlash(true);
      setTimeout(() => setShowCoralFlash(false), 400);
    },
    [tLanding],
  );

  // Clear inspiration hint when user starts typing
  useEffect(() => {
    if (textContent.length > 0) {
      setInspirationHint(null);
    }
  }, [textContent]);

  // Handle validity changes from ContentInputArea
  const handleValidityChange = useCallback((isValid: boolean) => {
    setIsFormValid(isValid);
  }, []);

  // Handle edit text content from unified list
  const handleEditText = useCallback(
    (contentId: string, content: string) => {
      // Set the content in the textarea
      if (contentInputRef.current) {
        contentInputRef.current.setValue(content);
      }
      // Update local state for auto-save
      setTextContent(content);
      // Set the content ID so future saves update instead of create
      setEditingContent(contentId, content);
      // Scroll to the textarea
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [setEditingContent]
  );

  // Handle delete from unified panel - sync with local hooks
  const handleUnifiedPanelDelete = useCallback(
    (contentId: string) => {
      // Remove from all local hook states that might have this content
      removeFileByContentId(contentId);
      removeImageByContentId(contentId);
      removeUrlByContentId(contentId);
      removeGoogleDocByContentId(contentId);
    },
    [removeFileByContentId, removeImageByContentId, removeUrlByContentId, removeGoogleDocByContentId]
  );

  // Show processing indicator while creating
  if (isPending) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <ProcessingIndicator />
      </Container>
    );
  }

  // Determine if value props should be collapsed
  // Collapse when: user has started creating content OR is in later phases OR is returning user OR returned from testing OR returned from clarification
  const shouldCollapseValueProps = hasContent || isFormValid || creationPhase !== 'content-input' || isSessionRecovery || returnedFromTesting || returnedFromClarification;

  // Content input form (reusable across layouts)
  const contentInputForm = (
    <Box
      onPaste={handleContentPaste}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 2, md: 3 },
      }}
    >
      {/* Text content input */}
      <ContentInputArea
        onSubmit={handleSubmit}
        isSubmitting={isPending}
        hookRef={contentInputRef}
        onValidityChange={handleValidityChange}
        onContentChange={setTextContent}
        autoFocus={shouldCollapseValueProps}
        inspirationHint={inspirationHint}
        showCoralFlash={showCoralFlash}
        actionSlot={
          textSavedContentId && isEditingText ? (
            <Button
              variant="text"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                resetEditing();
                contentInputRef.current?.reset();
                setTextContent('');
              }}
            >
              {tContent('addNewText')}
            </Button>
          ) : undefined
        }
      />

      {/* File upload zone (handles both documents and images) */}
      <FileUploadZone
        onFilesAccepted={handleFilesAccepted}
        onFilesRejected={(rejections) => {
          // Route rejections to appropriate handler based on file type
          const imageRejections = rejections.filter((r) =>
            r.file.type.startsWith('image/')
          );
          const fileRejections = rejections.filter(
            (r) => !r.file.type.startsWith('image/')
          );

          if (fileRejections.length > 0) {
            handleRejectedFiles(fileRejections);
          }
          if (imageRejections.length > 0) {
            handleRejectedImages(imageRejections);
          }
        }}
      />

      {/* Inline upload previews - hidden on desktop when left sidebar shows all sources */}
      {!(isDesktop && shouldCollapseValueProps) && (
        <CollapsibleUploadPreview
          filesCount={files.length}
          imagesCount={images.length}
          urlsCount={0}
          googleDocsCount={0}
          isProcessing={isUploadingFiles || isUploadingImages || isProcessingImages}
        >
          {/* Uploaded files list (documents) */}
          <FilePreviewList
            files={files}
            onRemove={removeFile}
            onProcessWithAI={processWithAI}
          />

          {/* Uploaded images list */}
          <ImagePreviewList
            images={images}
            onRemove={removeImage}
            onRetry={retryImage}
          />
        </CollapsibleUploadPreview>
      )}

      {/* URL input field */}
      <UrlInputField
        onSubmit={handleUrlSubmit}
        isLoading={isExtracting || isExtractingGoogle}
      />

      {/* Inline URL previews - hidden on desktop when left sidebar shows all sources */}
      {!(isDesktop && shouldCollapseValueProps) && (
        <>
          {/* URL extraction progress */}
          <UrlCrawlProgress
            isExtracting={isExtracting}
            currentCount={currentCount}
            totalCount={totalCount}
            summary={extractionSummary}
          />

          {/* Collapsible URL/Google Docs previews */}
          <CollapsibleUploadPreview
            filesCount={0}
            imagesCount={0}
            urlsCount={urls.length}
            googleDocsCount={googleDocs.length}
            isProcessing={isExtracting || isExtractingGoogle}
          >
            {/* Extracted URLs list */}
            <UrlPreviewList
              urls={urls}
              onRemove={removeUrl}
            />

            {/* Extracted Google documents list */}
            <GoogleDocPreviewList
              contents={googleDocs}
              onRemove={removeGoogleDoc}
            />
          </CollapsibleUploadPreview>
        </>
      )}

      {/* Create Bot button - visible from start, disabled until content is added */}
      {/* Only show when no database content yet AND no text being typed/saved */}
      {/* Hide as soon as user starts typing to prevent flash before UnifiedContentPanel takes over */}
      {!combinedBotId && !optimisticText && !isSavingText && textContent.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleContinue}
            disabled={!isFormValid && !hasContent}
            sx={{
              px: 5,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: 2,
              minWidth: 200,
            }}
          >
            {tLanding('contentSection.createButton')}
          </Button>
        </Box>
      )}

      {/* Unified content panel - shows database content with summary and proceed */}
      {/* On desktop, inline list is hidden (shown in left sidebar instead) */}
      <UnifiedContentPanel
        botId={combinedBotId}
        onProceed={returnedFromTesting ? handleReturnToTesting : handleContinue}
        onEditText={handleEditText}
        onDelete={handleUnifiedPanelDelete}
        disabled={isPending || isUploading}
        isSessionRecovery={isSessionRecovery}
        proceedButtonLabel={returnedFromTesting ? tTesting('actions.returnToTesting') : undefined}
        proceedButtonIcon={returnedFromTesting ? <ArrowBackIcon /> : undefined}
        hideInlineList={isDesktop && shouldCollapseValueProps}
        optimisticText={optimisticText}
      />

    </Box>
  );

  // Left sidebar content for variants (shared across all variants)
  const leftSidebarContent = (
    <>
      {/* Transient progress/error section (uploads, extractions in flight) */}
      <SidebarProgressSection
        files={files}
        isUploadingFiles={isUploadingFiles}
        onRemoveFile={removeFile}
        images={images}
        isUploadingImages={isUploadingImages}
        isProcessingImages={isProcessingImages}
        onRemoveImage={removeImage}
        isExtracting={isExtracting}
        currentCount={currentCount}
        totalCount={totalCount}
        extractionSummary={extractionSummary}
        urlErrors={urls.filter(u => u.status === 'error')}
        onRemoveUrl={removeUrl}
        googleDocErrors={googleDocs.filter(g => g.status === 'error')}
        onRemoveGoogleDoc={removeGoogleDoc}
      />
      {/* Persisted DB sources list */}
      <LeftSidebarSourcesList
        botId={combinedBotId}
        onEditText={handleEditText}
        onDelete={handleUnifiedPanelDelete}
        disabled={isPending || isUploading}
        isSessionRecovery={isSessionRecovery}
        optimisticText={optimisticText}
        isSaving={isSavingText}
      />
    </>
  );

  // Render the appropriate A/B variant layout (Story 6.1)
  const renderVariantLayout = () => {
    // During variant loading, show the default layout (Variant C is closest to original)
    if (isVariantLoading) {
      return (
        <LandingLayout
          collapsed={shouldCollapseValueProps}
          leftContent={leftSidebarContent}
        >
          {contentInputForm}
        </LandingLayout>
      );
    }

    // Render based on assigned variant
    switch (variant) {
      case 'A':
        return (
          <VariantA
            collapsed={shouldCollapseValueProps}
            leftContent={leftSidebarContent}
            onIdeaClick={handleIdeaClick}
          >
            {contentInputForm}
          </VariantA>
        );
      case 'B':
        return (
          <VariantB
            collapsed={shouldCollapseValueProps}
            leftContent={leftSidebarContent}
            onIdeaClick={handleIdeaClick}
          >
            {contentInputForm}
          </VariantB>
        );
      case 'C':
      default:
        return (
          <VariantC
            collapsed={shouldCollapseValueProps}
            leftContent={leftSidebarContent}
            onIdeaClick={handleIdeaClick}
          >
            {contentInputForm}
          </VariantC>
        );
    }
  };

  // Show header/footer across all creation phases
  const showHeaderFooter = true;

  // Schema.org SoftwareApplication — landing page only (Story 6.15)
  const softwareAppJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Boty',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    description: 'Create AI chatbots in minutes from your content — no coding required',
    url: process.env.NEXT_PUBLIC_BASE_URL || 'https://boty.bot',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(softwareAppJsonLd) }}
      />
      {/* Landing Header - only shown in content-input phase (Story 6.7) */}
      {showHeaderFooter && <LandingHeader showSignIn={!user} />}

      <Container maxWidth="lg" sx={{ py: { xs: 1, md: 2 }, flex: 1 }}>
      {/* Testing phase - centered, no landing layout */}
      {creationPhase === 'testing' && combinedBotId ? (
        <Box sx={{ maxWidth: 700, mx: 'auto' }}>
          <TestingPhase
            botId={combinedBotId}
            onAddContent={handleAddContentFromTesting}
            onComplete={handleTestingComplete}
            onBack={handleBackFromTesting}
            contentVersion={contentVersion}
            onQuestionCountChange={setTestQuestionCount}
          />
        </Box>
      ) : creationPhase === 'clarification' && combinedBotId ? (
        // Clarification phase - centered, no landing layout
        <Box sx={{ maxWidth: 700, mx: 'auto' }}>
          <ClarificationQuestions
            botId={combinedBotId}
            onComplete={handleClarificationComplete}
            onBack={handleBackToContent}
          />
        </Box>
      ) : user ? (
        // Authenticated user - two-column with sources sidebar on desktop, centered on mobile
        <Box
          sx={isDesktop && shouldCollapseValueProps ? {
            display: 'flex',
            gap: 6,
            width: '100%',
            alignItems: 'flex-start',
          } : {
            maxWidth: 600,
            mx: 'auto',
          }}
        >
          {/* Left sidebar with sources list - desktop only, after content added */}
          {isDesktop && shouldCollapseValueProps && (
            <Box
              sx={{
                flex: '0 0 380px',
                maxWidth: 380,
                position: 'sticky',
                top: 24,
                maxHeight: 'calc(100vh - 48px)',
                overflowY: 'auto',
              }}
            >
              {leftSidebarContent}
            </Box>
          )}
          {/* Creation form */}
          <Box
            sx={{
              ...(isDesktop && shouldCollapseValueProps ? { flex: 1, maxWidth: 600 } : {}),
              bgcolor: 'background.paper',
              borderRadius: 3,
              p: { xs: 2, md: 3 },
              boxShadow: 1,
            }}
          >
            <Typography
              variant="h4"
              fontWeight={600}
              color="primary.main"
              sx={{ mb: 1 }}
            >
              {tLanding('contentSection.titleAuthenticated')}
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 3 }}
            >
              {tLanding('contentSection.subtitle')}
            </Typography>
            {contentInputForm}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 3, textAlign: 'center' }}
            >
              {tLanding('contentSection.helperText')}
            </Typography>
          </Box>
        </Box>
      ) : (
        // Non-authenticated user - A/B variant layout with marketing content (Story 6.1)
        <>
          {renderVariantLayout()}
          {/* Use cases surfaced out of the footer (Story 19.50 + 19.51).
              Only while value props are visible (content-input, not yet creating). */}
          {!shouldCollapseValueProps && <UseCasesSection />}
        </>
      )}

      {/* Celebration Modal - shown after clarification phase completes */}
      <CelebrationModal
        open={showCelebration}
        onProceedToTesting={handleCelebrationProceed}
        onShare={handleShare}
      />

      {/* Auto-refresh prompt dialog - shown after URL extraction completes */}
      <AutoRefreshPromptDialog
        open={refreshPrompt.open}
        onClose={() =>
          setRefreshPrompt({ open: false, contentId: '', sourceUrl: '' })
        }
        onSave={handleRefreshSave}
        isSaving={updateRefreshSettings.isPending}
        sourceUrl={refreshPrompt.sourceUrl}
      />

      {/* Sign-up prompt for engaged unauthenticated users (Story 3.8) */}
      {shouldShowPrompt && promptType && (
        <SignUpPrompt
          promptType={promptType}
          onDismiss={(remindLater) => dismissPrompt(promptType, remindLater)}
        />
      )}
      </Container>

      {/* Landing Footer - only shown in content-input phase (Story 6.7) */}
      {showHeaderFooter && <LandingFooter />}
    </Box>
  );
}
